import { Check, Film } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services } from '../data/mock';

const SceneProduction = () => {
  const productionService = services[2];

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-red-600/10 flex items-center justify-center">
              <Film className="text-red-500" size={36} />
            </div>
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-red-500 mb-6 uppercase">
            Scene Film Production
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed">
            {productionService.description}
          </p>
        </div>

        {/* Hero Image */}
        <div className="max-w-6xl mx-auto mb-20">
          <img 
            src="https://images.unsplash.com/photo-1577190651915-bf62d54d5b36" 
            alt="Film production"
            className="w-full h-96 object-cover rounded-lg"
          />
        </div>

        {/* Approach */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Our Approach</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                <Check className="text-red-500" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Creative Planning & Collaboration</h3>
                <p className="text-gray-400 text-sm">We work closely with directors, producers, and creative teams to understand your narrative vision, developing detailed scene breakdowns and production schedules that align with your creative and logistical requirements.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                <Check className="text-red-500" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Cinematic Lighting & Mood</h3>
                <p className="text-gray-400 text-sm">Professional lighting design tailored to your scene's emotional tone and narrative requirements. From dramatic noir aesthetics to subtle naturalistic lighting, we create the perfect atmosphere for your story.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                <Check className="text-red-500" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Clear Boundaries & Professionalism</h3>
                <p className="text-gray-400 text-sm">Every production operates under clearly defined parameters with established protocols, professional conduct standards, and transparent communication ensuring smooth collaboration and mutual respect throughout the process.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-zinc-900 rounded-lg p-6 border border-red-900/20">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-600/10 flex items-center justify-center">
                <Check className="text-red-500" size={20} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Respectful, Consent-First Approach</h3>
                <p className="text-gray-400 text-sm">Mandatory consent protocols for all performers and participants with clear safe words, established boundaries, and the ability to pause or stop at any time. Professional standards and participant safety are non-negotiable priorities.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Production Values */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Production Excellence</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-zinc-900 rounded-lg p-8 mb-4 border border-red-900/20">
                <h3 className="text-2xl font-bold text-red-500 mb-2">Cinematic Lighting</h3>
                <p className="text-gray-400">Professional lighting rigs and mood control for atmospheric storytelling</p>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-zinc-900 rounded-lg p-8 mb-4 border border-red-900/20">
                <h3 className="text-2xl font-bold text-red-500 mb-2">Story-Driven</h3>
                <p className="text-gray-400">Collaborative creative planning focused on narrative and visual impact</p>
              </div>
            </div>
            <div className="text-center">
              <div className="bg-zinc-900 rounded-lg p-8 mb-4 border border-red-900/20">
                <h3 className="text-2xl font-bold text-red-500 mb-2">Professional Sets</h3>
                <p className="text-gray-400">Controlled filming environments designed for your production needs</p>
              </div>
            </div>
          </div>
        </div>

        {/* Equipment Gallery */}
        <div className="max-w-6xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Professional Equipment</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <img 
              src="https://images.unsplash.com/photo-1762028895833-bfcf6c6052cb" 
              alt="Studio lighting"
              className="w-full h-80 object-cover rounded-lg"
            />
            <img 
              src="https://images.unsplash.com/photo-1619850015546-84a1c7b7aed0" 
              alt="Camera equipment"
              className="w-full h-80 object-cover rounded-lg"
            />
            <img 
              src="https://images.unsplash.com/photo-1762028894947-427157955fea" 
              alt="Lighting array"
              className="w-full h-80 object-cover rounded-lg"
            />
          </div>
        </div>

        {/* Pricing */}
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-3xl font-semibold text-white mb-8 text-center">Production Packages</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {productionService.pricing.map((pkg, index) => (
              <div 
                key={index} 
                className="bg-zinc-900 rounded-lg p-8 border border-red-900/20 hover:border-red-600/50 transition-all text-center"
              >
                <h3 className="text-xl font-semibold text-white mb-2">{pkg.package}</h3>
                <p className="text-4xl font-bold text-red-500 my-6">{pkg.price}</p>
                <Link 
                  to="/contact"
                  className="block w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
                >
                  {pkg.price === 'Contact' ? 'Discuss Project' : 'Book Now'}
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-semibold text-white mb-4">Start Your Production</h2>
          <p className="text-gray-400 mb-8">Let's discuss your creative vision</p>
          <Link 
            to="/contact"
            className="inline-block bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-mono text-sm tracking-widest uppercase transition-all"
          >
            Get in Touch
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SceneProduction;